"use client";

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { toast } from "react-toastify";
import { useInviteEverifyUser } from "../hooks/superAdminEverify/useInviteEverifyUser";
import { useUpdateEverifyUser } from "../hooks/superAdminEverify/useUpdateEverifyUser";
import { useGetEverifyCompanies } from "../hooks/superAdminEverify/useGetEverifyCompanies";

export const AddUserModal = ({ isOpen, onClose, editData }) => {
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    jobTitle: "",
    phoneNumber: "",
  });

  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [rolesByCompany, setRolesByCompany] = useState({}); // { companyId: 6 | 7 }
  const [statusByCompany, setStatusByCompany] = useState({}); // { companyId: 0 | 1 | 2 }

  const {
    data: companies = [],
    isLoading: companiesLoading,
  } = useGetEverifyCompanies();
  const inviteEverify = useInviteEverifyUser();
  const updateEverify = useUpdateEverifyUser();

  useEffect(() => {
    if (editData) {
      setUser({
        firstName: editData.firstName || "",
        lastName: editData.lastName || "",
        email: editData.email || "",
        jobTitle: editData.jobTitle || "",
        phoneNumber: editData.phoneNumber || "",
      });

      if (Array.isArray(editData.userCompany)) {
        const ids = editData.userCompany.map((uc) => Number(uc.companyId));
        const roleMap = {};
        const statusMap = {};
        editData.userCompany.forEach((uc) => {
          const id = Number(uc.companyId);
          roleMap[id] = uc.roleId;
          statusMap[id] = uc.status ?? editData.status ?? 0;
        });
        setSelectedCompanyIds(ids);
        setRolesByCompany(roleMap);
        setStatusByCompany(statusMap);
      }
    } else {
      setUser({
        firstName: "",
        lastName: "",
        email: "",
        jobTitle: "",
        phoneNumber: "",
      });
      setSelectedCompanyIds([]);
      setRolesByCompany({});
      setStatusByCompany({});
    }
  }, [editData]);

  const handleChangeField = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeCompanies = (event) => {
    const { value } = event.target;
    const normalized = (typeof value === "string" ? value.split(",") : value)
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

    setSelectedCompanyIds(normalized);
    setRolesByCompany((prev) => {
      const next = {};
      normalized.forEach((id) => {
        next[id] = prev[id] ?? 6;
      });
      return next;
    });
  };

  const handleRoleChange = (companyId, roleId) => {
    setRolesByCompany((prev) => ({ ...prev, [companyId]: roleId }));
  };

  const isValid = () => {
    if (!user.firstName || !user.lastName || !user.email || !user.jobTitle)
      return false;
    if (selectedCompanyIds.length === 0) return false;
    for (const cid of selectedCompanyIds) {
      if (!rolesByCompany[cid]) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      toast.error("Please fill all required fields and select companies/roles.");
      return;
    }

    const id = toast.loading(editData ? "Updating user..." : "Creating user...");
    const primaryCompanyId = Number(selectedCompanyIds[0]);
    if (!primaryCompanyId) {
      toast.update(id, {
        render: "Select at least one company.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      return;
    }

    try {
      if (editData) {
        const userCompany = selectedCompanyIds.map((cid) => ({
          companyId: Number(cid),
          roleId: Number(rolesByCompany[cid]),
          status: statusByCompany[cid] ?? 0,
        }));
        await updateEverify.mutateAsync({
          companyId: primaryCompanyId,
          body: {
            user: {
              id: editData.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              jobTitle: user.jobTitle,
              resendEmail: false,
            },
            userCompany,
          },
        });
      } else {
        const userCompany = selectedCompanyIds.map((cid) => ({
          companyId: Number(cid),
          roleId: Number(rolesByCompany[cid]),
        }));
        await inviteEverify.mutateAsync({
          companyId: primaryCompanyId,
          body: { user, userCompany, phoneNumber: user.phoneNumber },
        });
      }
      toast.update(id, {
        render: editData ? "User updated" : "User created",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      onClose();
    } catch (e) {
      toast.update(id, {
        render: "Failed to save user",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px"
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          {editData ? "Edit User" : "Add User"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                name="firstName"
                value={user.firstName}
                onChange={handleChangeField}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                name="lastName"
                value={user.lastName}
                onChange={handleChangeField}
                fullWidth
                size="small"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                value={user.email}
                onChange={handleChangeField}
                fullWidth
                size="small"
                required
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                name="phoneNumber"
                value={user.phoneNumber}
                onChange={handleChangeField}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Job Title"
                name="jobTitle"
                value={user.jobTitle}
                onChange={handleChangeField}
                fullWidth
                size="small"
                required
              />
            </Grid>

            <Grid item xs={12} sx={{ width: "84%" }}>
              <FormControl fullWidth required>
                <InputLabel>Companies</InputLabel>
                <Select
                  multiple
                  value={selectedCompanyIds}
                  onChange={handleChangeCompanies}
                  input={<OutlinedInput label="Companies" />}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return (
                        <Typography sx={{ color: "#9CA3AF" }}>
                          
                        </Typography>
                      );
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const comp = companies.find(
                            (c) => String(c.id) === String(value)
                          );
                          return (
                            <Chip
                              key={value}
                              label={comp?.name || value}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 300 },
                    },
                    disableScrollLock: true,
                  }}
                  required
                  displayEmpty
                  disabled={companiesLoading}
                >
                  {companiesLoading && (
                    <MenuItem disabled>Loading companies...</MenuItem>
                  )}
                  {!companiesLoading && companies.length === 0 && (
                    <MenuItem disabled>No companies available</MenuItem>
                  )}
                  {companies.map((company) => {
                    const loc = [company.address, company.city, company.state].filter(Boolean).join(", ");
                    return (
                      <MenuItem key={company.id} value={company.id}>
                        <div>
                          <div>{company.name}</div>
                          {loc && (
                            <div style={{ fontSize: "0.75em", color: "#6b7280" }}>{loc}</div>
                          )}
                        </div>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {selectedCompanyIds.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, mb: 1, mt: 1 }}
                >
                  Roles per company
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {selectedCompanyIds.map((cid) => {
                    const comp = companies.find(
                      (c) => String(c.id) === String(cid)
                    );
                    const role = rolesByCompany[cid] || 6;
                    return (
                      <Box
                        key={cid}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#F9FAFB",
                          borderRadius: "12px",
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                          {comp?.name || cid}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, fontSize: 13 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <input
                              type="radio"
                              name={`role-${cid}`}
                              checked={role === 6}
                              onChange={() => handleRoleChange(cid, 6)}
                            />
                            Company Admin
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <input
                              type="radio"
                              name={`role-${cid}`}
                              checked={role === 7}
                              onChange={() => handleRoleChange(cid, 7)}
                            />
                            HR User
                          </label>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pb: 3 }}>
        <button
          type="button"
          disabled={!isValid() || inviteEverify.isPending || updateEverify.isPending}
          onClick={handleSubmit}
          style={{
            border: "none",
            borderRadius: 999,
            backgroundColor:
              !isValid() || inviteEverify.isPending || updateEverify.isPending ? "#E5E7EB" : "#F7D154",
            color: "#111827",
            padding: "10px 18px",
            fontWeight: 600,
            fontSize: 14,
            cursor:
              !isValid() || inviteEverify.isPending || updateEverify.isPending ? "not-allowed" : "pointer",
          }}
        >
          {inviteEverify.isPending || updateEverify.isPending
            ? "Saving..."
            : editData
            ? "Update User"
            : "Add User"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
